import WhatsAppService from '../../../services/whatsapp.js';
import { showNotification, showModal, hideModal } from '../../../utils.js';

// Debounce utility to prevent rapid consecutive calls
const debounce = (fn, delay) => {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
};

export const setupWhatsAppModal = (company, onSuccess) => {
  const template = `
    <h3 class="text-lg font-bold mb-4">Управление подключением WhatsApp для ${company.name}</h3>
    <div id="whatsapp-status"></div>
    <div class="mt-4 flex space-x-3">
      <button id="pair-btn" class="px-4 py-2 bg-blue-600 text-white rounded">Подключить</button>
      <button id="disconnect-btn" class="px-4 py-2 bg-red-600 text-white rounded hidden">Отключить</button>
    </div>
  `;
  showModal(template);

  const statusEl = document.getElementById('whatsapp-status');
  const pairBtn = document.getElementById('pair-btn');
  const disconnectBtn = document.getElementById('disconnect-btn');

  // Track in-flight requests and last status
  let isFetchingStatus = false;
  let lastFetchedStatus = null;
  let abortController = null;

  const getStatusClass = (status) => {
    switch (status) {
      case 'connected':
        return 'text-green-600';
      case 'pending':
        return 'text-yellow-600';
      case 'disconnected':
      case 'unpaired':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'connected':
        return 'Подключено';
      case 'pending':
        return 'Ожидание подключения';
      case 'disconnected':
        return 'Не подключено';
      default:
        return 'Неизвестно';
    }
  };

  const updateStatusDisplay = async () => {
    if (isFetchingStatus) return lastFetchedStatus; // Skip if request in progress
    
    // Cancel any existing request
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();
    
    isFetchingStatus = true;
    try {
      const data = await WhatsAppService.getStatus(company.whatsapp_phone);
      lastFetchedStatus = data.status;
      const statusClass = getStatusClass(data.status);
      const statusText = getStatusText(data.status);
      
      statusEl.innerHTML = `
        <div class="mb-4">
          <p class="font-bold mb-2">Статус: <span class="${statusClass}">${statusText}</span></p>
          ${data.connection_code ? `
            <div class="bg-gray-100 p-3 rounded">
              <p class="text-sm text-gray-600 mb-1">Код подключения:</p>
              <p class="font-mono font-bold text-lg">${data.connection_code}</p>
            </div>
          ` : ''}
        </div>
      `;

      if (data.status === 'connected') {
        pairBtn.classList.add('hidden');
        disconnectBtn.classList.remove('hidden');
      } else {
        pairBtn.classList.remove('hidden');
        disconnectBtn.classList.add('hidden');
      }
      
      return data.status;
    } catch (error) {
      if (error.name === 'AbortError') {
        return lastFetchedStatus; // Return last status if request was aborted
      }
      
      // Handle 404 (not found) errors differently
      if (error.status === 404) {
        statusEl.innerHTML = `
          <div class="mb-4">
            <p class="text-gray-600">Для использования WhatsApp необходимо выполнить подключение</p>
          </div>
        `;
      } else {
        statusEl.innerHTML = `
          <div class="mb-4">
            <p class="text-red-600">Ошибка: ${error.message}</p>
            <button id="retry-btn" class="mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded">
              Повторить
            </button>
          </div>
        `;
        document.getElementById('retry-btn')?.addEventListener('click', debouncedUpdateStatus);
      }
      return 'error';
    } finally {
      isFetchingStatus = false;
      abortController = null;
    }
  };

  // Create debounced version of status update
  const debouncedUpdateStatus = debounce(updateStatusDisplay, 500);

  // Initial status check only if phone number exists and is not empty
  if (company.whatsapp_phone && company.whatsapp_phone.trim() !== '') {
    debouncedUpdateStatus();
  }

  // Pair button event
  pairBtn.addEventListener('click', async () => {
    try {
      const data = await WhatsAppService.pair(company.id, company.whatsapp_phone);
      showNotification('Подключение инициировано. Введите указанный ниже код в приложении WhatsApp в течение следующих 2 минут.');
      
      // Update UI with code and instructions
      statusEl.innerHTML = `
        <div class="mb-4">
          <p class="font-bold mb-2">Статус: <span class="text-yellow-600">Ожидание ввода кода</span></p>
          ${data.connection_code ? `
            <div class="bg-gray-100 p-3 rounded">
              <p class="text-sm text-gray-600 mb-1">Введите этот код в WhatsApp:</p>
              <p class="font-mono font-bold text-lg">${data.connection_code}</p>
              <p class="text-sm text-gray-600 mt-2">1. Откройте WhatsApp на своем телефоне</p>
              <p class="text-sm text-gray-600">2. Перейдите в Настройки > Связанные устройства</p>
              <p class="text-sm text-gray-600">3. Нажмите «Привязать устройство»</p>
              <p class="text-sm text-gray-600">4. Введите указанный выше код</p>
            </div>
          ` : ''}
        </div>
      `;
      pairBtn.classList.remove('hidden');
      disconnectBtn.classList.add('hidden');
      
      // Delay status check to give user time to enter code
      setTimeout(() => {
        let lastStatus = null;
        // Start periodic status checks
        const statusInterval = setInterval(async () => {
          const status = await debouncedUpdateStatus();
          
          // If status changed, update parent view
          if (status !== lastStatus) {
            lastStatus = status;
            // Add small delay to ensure backend has updated company data
            setTimeout(onSuccess, 1000); // Wait 1 second before refreshing companies view
          }
          
          if (status === 'connected') {
            clearInterval(statusInterval); // Stop checking once connected
          }
        }, 5000); // Check every 5 seconds

        // Clean up interval when modal is closed
        const closeBtn = document.querySelector('.modal-close');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => {
            clearInterval(statusInterval);
            // Add small delay to ensure backend has updated company data
            setTimeout(onSuccess, 1000); // Wait 1 second before final refresh
          });
        }
        
        // Additional cleanup on window unload
        window.addEventListener('beforeunload', () => {
          clearInterval(statusInterval);
          // Add small delay to ensure backend has updated company data
          setTimeout(onSuccess, 1000); // Wait 1 second before final refresh
        });
      }, 30000); // Wait 30 seconds before starting status checks
      
      onSuccess(); // if needed to refresh parent view
    } catch (error) {
      // Handle specific validation error from backend
      if (error.response?.status === 400) {
        showNotification(error.response.data.detail, 'error');
        // Update UI to show error state
        statusEl.innerHTML = `
          <div class="mb-4">
            <p class="text-red-600">${error.response.data.detail}</p>
          </div>
        `;
      } else {
        showNotification(error.message, 'error');
      }
    }
  });

  // Disconnect button event
  disconnectBtn.addEventListener('click', async () => {
    try {
      await WhatsAppService.disconnect(company.whatsapp_phone);
      showNotification('WhatsApp отключен. Рассылка сообщений автоматически отключена.');
      
      // Check status after disconnecting
      await debouncedUpdateStatus();
      // Add small delay to ensure backend has updated company data
      setTimeout(onSuccess, 1000); // Wait 1 second before refreshing view
    } catch (error) {
      showNotification(error.message, 'error');
    }
  });

  // Cleanup function to handle all cleanup tasks
  const cleanup = () => {
    if (abortController) {
      abortController.abort();
    }
  };

  // Cleanup when modal is closed
  const closeBtn = document.querySelector('.modal-close');
  if (closeBtn) {
    closeBtn.addEventListener('click', cleanup);
  }

  // Additional cleanup on window unload
  window.addEventListener('beforeunload', cleanup);
};
